import React, { useEffect, useRef, useState } from 'react';
import { Box, Alert, CircularProgress, Typography } from '@mui/joy';

interface SimpleGoogleMapProps {
  center?: { lat: number; lng: number };
  zoom?: number;
  onLocationSelect?: (location: { lat: number; lng: number; address: string }) => void;
  selectedLocation?: { lat: number; lng: number };
  height?: string | number;
}

export const SimpleGoogleMap: React.FC<SimpleGoogleMapProps> = ({
  center = { lat: 39.8283, lng: -98.5795 }, // Center of US
  zoom = 10,
  onLocationSelect,
  selectedLocation,
  height = 400
}) => {
  console.log('SimpleGoogleMap: Component instantiated with props:', {
    center, zoom, selectedLocation, height, onLocationSelect: !!onLocationSelect
  });
  const [mapContainer, setMapContainer] = useState<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mapInitialized, setMapInitialized] = useState(false);

  // Callback ref that stores the element in state
  const mapRefCallback = (element: HTMLDivElement | null) => {
    console.log('SimpleGoogleMap: Ref callback called with element:', element);
    console.log('SimpleGoogleMap: Element details:', {
      element,
      tagName: element?.tagName,
      clientHeight: element?.clientHeight,
      clientWidth: element?.clientWidth
    });
    setMapContainer(element);
  };

  useEffect(() => {
    console.log('SimpleGoogleMap: useEffect called with mapContainer:', mapContainer);
    if (!mapContainer) {
      console.log('SimpleGoogleMap: No map container available yet');
      return;
    }

    let isMounted = true;
    setLoading(true);
    
    const initMap = () => {
      if (!isMounted) return;
      
      console.log('SimpleGoogleMap: Starting initialization...', {
        googleAvailable: !!(window.google && window.google.maps),
        mapContainerExists: !!mapContainer,
        mapContainerElement: mapContainer
      });
      
      if (!window.google || !window.google.maps) {
        console.log('SimpleGoogleMap: Google Maps API not ready, retrying...');
        setTimeout(initMap, 500);
        return;
      }

      try {
        console.log('SimpleGoogleMap: Creating map instance...');
        
        const mapOptions: google.maps.MapOptions = {
          center: selectedLocation || center,
          zoom: selectedLocation ? 21 : zoom, // Maximum zoom for closest view
          mapTypeId: google.maps.MapTypeId.SATELLITE,
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true,
          zoomControl: true,
          tilt: 0, // Force top-down view (no tilted perspective)
        };

        const map = new google.maps.Map(mapContainer, mapOptions);
        mapInstanceRef.current = map;

        // Add click listener
        if (onLocationSelect) {
          map.addListener('click', (event: google.maps.MapMouseEvent) => {
            if (event.latLng) {
              const lat = event.latLng.lat();
              const lng = event.latLng.lng();
              const address = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
              onLocationSelect({ lat, lng, address });
            }
          });
        }

        // Add marker if we have a selected location
        if (selectedLocation) {
          addMarker(selectedLocation);
        }

        console.log('SimpleGoogleMap: Map initialized successfully');
        setMapInitialized(true);
        setLoading(false);
        setError(null);
      } catch (err) {
        console.error('SimpleGoogleMap: Error initializing map:', err);
        setError(`Failed to initialize map: ${err instanceof Error ? err.message : 'Unknown error'}`);
        setLoading(false);
      }
    };

    // Start initialization
    initMap();

    return () => {
      isMounted = false;
    };
  }, [mapContainer]); // Re-run when mapContainer changes

  const addMarker = (location: { lat: number; lng: number }) => {
    if (!mapInstanceRef.current) return;

    // Remove existing marker
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }

    // Add new marker
    markerRef.current = new google.maps.Marker({
      position: location,
      map: mapInstanceRef.current,
      title: 'Selected Property Location',
    });

    // Center map on marker
    mapInstanceRef.current.setCenter(location);
  };

  // Update map when selectedLocation changes
  useEffect(() => {
    if (selectedLocation && mapInstanceRef.current) {
      console.log('SimpleGoogleMap: Updating marker location');
      addMarker(selectedLocation);
    }
  }, [selectedLocation]);

  console.log('SimpleGoogleMap: About to render component with state:', { 
    loading, error, mapContainer, selectedLocation, height, mapInitialized
  });

  return (
    <Box 
      sx={{ 
        height,
        width: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 'sm',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      <div 
        ref={mapRefCallback} 
        style={{ 
          height: '100%', 
          width: '100%'
        }} 
      />
      
      {/* Loading overlay */}
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 1000
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography level="body-sm">Loading map...</Typography>
          </Box>
        </Box>
      )}
      
      {/* Error overlay */}
      {error && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            zIndex: 1000
          }}
        >
          <Alert color="danger">{error}</Alert>
        </Box>
      )}
    </Box>
  );
};